import { Tag, TagCreateInput, TagGroup, TagGroupCreateInput } from '@vivport/backend/types';
import { useMemo, useState } from 'react';
import {
  DndContext,
  closestCenter,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Building, GripVertical, Layers, Lock, Plus, Search, Tag as TagIcon, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { AppLink } from '@/components/ui/app-link';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useTagCreate,
  useTagDelete,
  useTagReorder,
  useTags,
  useTagGroupCreate,
  useTagGroupDelete,
  useTagGroupReorder,
  useTagGroups,
} from '@/hooks';
import { ProvenanceAttribution } from '@/components/work4me/ProvenanceAttribution';
import { getAxiosErrorMessage } from '@/utils/error-utils';
import { Work4MeTagCreate } from './Work4MeTagCreate';
import { Work4MeTagGroupCreate } from './Work4MeTagGroupCreate';

type SortBy = 'sort-order' | 'name-asc' | 'name-desc' | 'updated-desc' | 'updated-asc';
interface GroupSection {
  group: TagGroup;
  tags: Tag[];
}

function sortByMode<T extends { name: string; updatedAt: string }>(
  items: T[],
  sortBy: SortBy,
  orderValue: (item: T) => number | null | undefined
): T[] {
  if (sortBy === 'sort-order') {
    return [...items].sort((a, b) => (orderValue(a) ?? 99999) - (orderValue(b) ?? 99999));
  }
  return [...items].sort((a, b) => {
    if (sortBy === 'name-asc') return a.name.localeCompare(b.name);
    if (sortBy === 'name-desc') return b.name.localeCompare(a.name);
    if (sortBy === 'updated-asc') return new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });
}

interface GroupRowProps {
  group: TagGroup;
  description: string;
  canDrag: boolean;
  onOpenGroup: (group: TagGroup) => void;
  onDelete: (group: TagGroup) => void;
}

function GroupRow({ group, description, canDrag, onOpenGroup, onDelete }: GroupRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `group-${group.id}`,
    disabled: !canDrag,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid="work4me-tag-group-entry"
      data-tag-group-name={group.name}
      className={`flex items-center gap-1.5 px-4 py-2 transition-colors cursor-pointer ${isDragging ? 'opacity-50 bg-muted/60' : 'hover:bg-muted/40'}`}
      role="link"
      tabIndex={0}
      onClick={() => onOpenGroup(group)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpenGroup(group);
        }
      }}
    >
      {canDrag ? (
        <button
          type="button"
          className="flex-shrink-0 cursor-grab active:cursor-grabbing p-0.5 -ml-0.5 text-muted-foreground hover:text-foreground touch-none"
          onClick={(event) => event.stopPropagation()}
          {...attributes}
          {...listeners}
          aria-label="Drag group to reorder"
        >
          <GripVertical className="h-3.5 w-3.5 text-muted-foreground" />
        </button>
      ) : (
        <div className="w-5 flex-shrink-0" aria-hidden />
      )}
      <div className="w-4 h-4 shrink-0 text-muted-foreground">
        <Layers className="h-3.5 w-3.5" />
      </div>
      <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-1.5">
          <AppLink
            to={`/work4me/tag-groups/${group.id}`}
            className="font-medium text-sm truncate text-primary hover:underline"
          >
            {group.name}
          </AppLink>
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge variant="outline" className="h-5 px-1.5 text-[10px] leading-none">{group.selectionMode}</Badge>
          <Badge variant="secondary" className="h-5 px-1.5 text-[10px] leading-none">
            {group.ownerScope === 'HUB' ? <Building className="h-3 w-3 mr-1" /> : <Lock className="h-3 w-3 mr-1" />}
            {group.ownerScope}
          </Badge>
          <span className="text-[11px] text-muted-foreground truncate">{description}</span>
          <ProvenanceAttribution lastChange={group.lastChange} fallbackDate={group.updatedAt} />
        </div>
      </div>
      <div className="flex items-center gap-0.5 flex-shrink-0">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={(event) => {
            event.stopPropagation();
            onDelete(group);
          }}
          aria-label="Delete"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

interface TagRowProps {
  tag: Tag;
  canDrag: boolean;
  onOpenTag: (tag: Tag) => void;
  onDelete: (tag: Tag) => void;
}

function TagRow({ tag, canDrag, onOpenTag, onDelete }: TagRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `tag-${tag.id}`,
    disabled: !canDrag,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      data-testid="work4me-tag-entry"
      data-tag-name={tag.name}
      className={`transition-colors ${isDragging ? 'opacity-50 bg-muted/60' : 'hover:bg-muted/40'}`}
    >
      <div className="ml-12 border-t" />
      <div
        className="flex items-center gap-1.5 pl-12 pr-4 py-2 cursor-pointer"
        role="link"
        tabIndex={0}
        onClick={() => onOpenTag(tag)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onOpenTag(tag);
          }
        }}
      >
        {canDrag ? (
          <button
            type="button"
            className="flex-shrink-0 cursor-grab active:cursor-grabbing p-0.5 -ml-0.5 text-muted-foreground hover:text-foreground touch-none"
            onClick={(event) => event.stopPropagation()}
            {...attributes}
            {...listeners}
            aria-label="Drag tag to reorder"
          >
            <GripVertical className="h-3.5 w-3.5 text-muted-foreground" />
          </button>
        ) : (
          <div className="w-5 flex-shrink-0" aria-hidden />
        )}
        <div className="w-4 h-4 shrink-0 text-muted-foreground">
          <TagIcon className="h-3.5 w-3.5" />
        </div>
        <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-1.5">
          <span className="font-medium text-sm truncate text-primary hover:underline">{tag.name}</span>
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge variant="secondary" className="h-5 px-1.5 text-[10px] leading-none">
              {tag.ownerScope === 'HUB' ? <Building className="h-3 w-3 mr-1" /> : <Lock className="h-3 w-3 mr-1" />}
              {tag.ownerScope}
            </Badge>
            <span className="text-[11px] text-muted-foreground truncate">{tag.description ?? 'No description'}</span>
            <ProvenanceAttribution lastChange={tag.lastChange} fallbackDate={tag.updatedAt} />
          </div>
        </div>
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={(event) => {
              event.stopPropagation();
              onDelete(tag);
            }}
            aria-label="Delete"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </li>
  );
}

export function Work4MeTagList() {
  const navigate = useNavigate();
  const [showNewTagDialog, setShowNewTagDialog] = useState(false);
  const [showNewTagGroupDialog, setShowNewTagGroupDialog] = useState(false);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<SortBy>('sort-order');

  const { data: tags = [], isLoading: tagsLoading, isError: tagsError } = useTags();
  const { data: tagGroups = [], isLoading: groupsLoading, isError: groupsError } = useTagGroups();
  const createTagMutation = useTagCreate();
  const deleteTagMutation = useTagDelete();
  const reorderTagMutation = useTagReorder();
  const createGroupMutation = useTagGroupCreate();
  const deleteGroupMutation = useTagGroupDelete();
  const reorderGroupMutation = useTagGroupReorder();

  const normalizedSearch = search.trim().toLowerCase();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const sortedGroups = useMemo(
    () => sortByMode(tagGroups, sortBy, (group) => group.groupOrder),
    [tagGroups, sortBy]
  );

  const sortedTags = useMemo(
    () => sortByMode(tags, sortBy, (tag) => tag.tagOrder),
    [tags, sortBy]
  );

  const tagsByGroup = useMemo(() => {
    const map = new Map<number | null, Tag[]>();
    for (const tag of sortedTags) {
      const key = tag.tagGroupId ?? null;
      const list = map.get(key) ?? [];
      list.push(tag);
      map.set(key, list);
    }
    return map;
  }, [sortedTags]);

  const visibleGroupSections = useMemo<GroupSection[]>(() => {
    return sortedGroups
      .map((group) => {
        const groupMatches = !normalizedSearch
          || group.name.toLowerCase().includes(normalizedSearch)
          || (group.description ?? '').toLowerCase().includes(normalizedSearch);
        const groupTags = (tagsByGroup.get(group.id) ?? []).filter((tag) => {
          if (groupMatches) return true;
          return tag.name.toLowerCase().includes(normalizedSearch)
            || (tag.description ?? '').toLowerCase().includes(normalizedSearch);
        });
        if (normalizedSearch && !groupMatches && groupTags.length === 0) {
          return null;
        }
        return {
          group,
          tags: groupTags,
        };
      })
      .filter((value): value is GroupSection => value !== null);
  }, [normalizedSearch, sortedGroups, tagsByGroup]);

  const visibleUngroupedTags = useMemo(() => {
    const ungrouped = tagsByGroup.get(null) ?? [];
    if (!normalizedSearch) return ungrouped;
    return ungrouped.filter((tag) => {
      return tag.name.toLowerCase().includes(normalizedSearch)
        || (tag.description ?? '').toLowerCase().includes(normalizedSearch)
        || 'ungrouped'.includes(normalizedSearch);
    });
  }, [normalizedSearch, tagsByGroup]);

  const isBusy = tagsLoading || groupsLoading;
  const isError = tagsError || groupsError;
  const isEmpty = !isBusy && !isError && visibleGroupSections.length === 0 && visibleUngroupedTags.length === 0;
  const canDrag = normalizedSearch.length === 0 && !reorderGroupMutation.isPending && !reorderTagMutation.isPending;

  const handleTagCreated = async (input: TagCreateInput) => {
    await createTagMutation.mutateAsync(input, {
      onError: (error) => toast.error(getAxiosErrorMessage(error) ?? 'Failed to create tag'),
    });
  };

  const handleTagGroupCreated = async (input: TagGroupCreateInput) => {
    await createGroupMutation.mutateAsync(input, {
      onError: (error) => toast.error(getAxiosErrorMessage(error) ?? 'Failed to create tag group'),
    });
  };

  const handleDeleteTag = async (tag: Tag) => {
    await deleteTagMutation.mutateAsync(tag.id, {
      onError: (error) => toast.error(getAxiosErrorMessage(error) ?? 'Failed to delete tag'),
    });
  };

  const handleDeleteGroup = async (group: TagGroup) => {
    await deleteGroupMutation.mutateAsync(group.id, {
      onError: (error) => toast.error(getAxiosErrorMessage(error) ?? 'Failed to delete tag group'),
    });
  };

  const handleGroupDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!canDrag || !over || active.id === over.id) return;

    const parseId = (value: string) => Number(value.replace('group-', ''));
    const activeId = parseId(String(active.id));
    const overId = parseId(String(over.id));
    const ids = sortedGroups.map((group) => group.id);
    const from = ids.indexOf(activeId);
    const to = ids.indexOf(overId);
    if (from < 0 || to < 0) return;

    const reordered = arrayMove(ids, from, to);
    await reorderGroupMutation.mutateAsync(reordered, {
      onError: (error) => toast.error(getAxiosErrorMessage(error) ?? 'Failed to reorder tag groups'),
    });
  };

  const handleTagDragEnd = async (event: DragEndEvent, tagGroupId: number | null, tagsInSection: Tag[]) => {
    const { active, over } = event;
    if (!canDrag || !over || active.id === over.id) return;

    const parseId = (value: string) => Number(value.replace('tag-', ''));
    const activeId = parseId(String(active.id));
    const overId = parseId(String(over.id));
    const ids = tagsInSection.map((tag) => tag.id);
    const from = ids.indexOf(activeId);
    const to = ids.indexOf(overId);
    if (from < 0 || to < 0) return;

    const reordered = arrayMove(ids, from, to);
    await reorderTagMutation.mutateAsync(
      { tagIds: reordered, tagGroupId },
      { onError: (error) => toast.error(getAxiosErrorMessage(error) ?? 'Failed to reorder tags') }
    );
  };

  return (
    <div data-testid="work4me-tags-management" className="-mx-4 py-2">
      <div className="px-4 py-3 border-b bg-muted/20">
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-xl font-light tracking-wide">Tags Management</h2>
            <div className="flex items-center gap-2 flex-wrap justify-end">
            <div className="relative w-[240px] sm:w-[280px]">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search groups and tags..."
                className="h-9 pl-10"
              />
            </div>
            <Select value={sortBy} onValueChange={(v) => setSortBy(v as SortBy)}>
              <SelectTrigger className="w-[140px] sm:w-[170px] h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="sort-order">Sort order</SelectItem>
                <SelectItem value="name-asc">Name (A-Z)</SelectItem>
                <SelectItem value="name-desc">Name (Z-A)</SelectItem>
                <SelectItem value="updated-desc">Updated (Newest)</SelectItem>
                <SelectItem value="updated-asc">Updated (Oldest)</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => setShowNewTagGroupDialog(true)} className="gap-2 h-9 px-3">
              <Plus className="h-4 w-4" />
              Tag Group
            </Button>
            <Button onClick={() => setShowNewTagDialog(true)} className="gap-2 h-9 px-3">
              <Plus className="h-4 w-4" />
              Tag
            </Button>
          </div>
          </div>
          <p className="text-muted-foreground">
            Manage process-flow Tag Groups and Tags for your Work4Me workspace.
          </p>
        </div>
      </div>

      {isBusy && (
        <div className="px-4 py-6 text-sm text-muted-foreground">Loading tags and groups...</div>
      )}

      {isError && (
        <div className="px-4 py-6 text-sm text-destructive">Unable to load tags and tag groups.</div>
      )}

      {isEmpty && (
        <div className="px-4 py-6 text-sm text-muted-foreground">
          {normalizedSearch ? 'No tag groups or tags match your search.' : 'No tag groups or tags yet.'}
        </div>
      )}

      {!isBusy && !isError && !isEmpty && (
        <div>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleGroupDragEnd}
          >
            <SortableContext
              items={visibleGroupSections.map(({ group }) => `group-${group.id}`)}
              strategy={verticalListSortingStrategy}
            >
              <ul className="divide-y">
                {visibleGroupSections.map(({ group, tags: groupedTags }) => (
                  <li key={group.id}>
                    <GroupRow
                      group={group}
                      description={group.description ?? 'No description'}
                      canDrag={canDrag}
                      onOpenGroup={(target) => navigate(`/work4me/tag-groups/${target.id}`)}
                      onDelete={handleDeleteGroup}
                    />

                    {groupedTags.length > 0 && (
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={(event) => handleTagDragEnd(event, group.id, groupedTags)}
                      >
                        <SortableContext
                          items={groupedTags.map((tag) => `tag-${tag.id}`)}
                          strategy={verticalListSortingStrategy}
                        >
                          <ul className="m-0 p-0">
                            {groupedTags.map((tag) => (
                              <TagRow
                                key={tag.id}
                                tag={tag}
                                canDrag={canDrag}
                                onOpenTag={(target) => navigate(`/work4me/tags/${target.id}`)}
                                onDelete={handleDeleteTag}
                              />
                            ))}
                          </ul>
                        </SortableContext>
                      </DndContext>
                    )}
                  </li>
                ))}

                {visibleUngroupedTags.length > 0 && (
                  <li>
                    <div className="flex items-center gap-1.5 px-4 py-2 hover:bg-muted/40">
                      <div className="w-4 -ml-0.5 flex-shrink-0" aria-hidden />
                      <div className="w-4 h-4 shrink-0 text-muted-foreground">
                        <Layers className="h-3.5 w-3.5" />
                      </div>
                      <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-1.5">
                        <span className="font-medium text-sm truncate">Ungrouped</span>
                        <span className="text-[11px] text-muted-foreground truncate">
                          Tags that do not belong to a group
                        </span>
                      </div>
                      <div className="flex items-center gap-0.5 flex-shrink-0" aria-hidden>
                        <div className="w-7 h-7" />
                        <div className="w-7 h-7" />
                      </div>
                    </div>
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={(event) => handleTagDragEnd(event, null, visibleUngroupedTags)}
                    >
                      <SortableContext
                        items={visibleUngroupedTags.map((tag) => `tag-${tag.id}`)}
                        strategy={verticalListSortingStrategy}
                      >
                        <ul className="m-0 p-0">
                          {visibleUngroupedTags.map((tag) => (
                            <TagRow
                              key={tag.id}
                              tag={tag}
                              canDrag={canDrag}
                              onOpenTag={(target) => navigate(`/work4me/tags/${target.id}`)}
                              onDelete={handleDeleteTag}
                            />
                          ))}
                        </ul>
                      </SortableContext>
                    </DndContext>
                  </li>
                )}
              </ul>
            </SortableContext>
          </DndContext>
        </div>
      )}

      <Work4MeTagCreate
        open={showNewTagDialog}
        onOpenChange={(open) => {
          setShowNewTagDialog(open);
        }}
        onTagCreated={handleTagCreated}
        tagGroups={tagGroups}
      />

      <Work4MeTagGroupCreate
        open={showNewTagGroupDialog}
        onOpenChange={setShowNewTagGroupDialog}
        onTagGroupCreated={handleTagGroupCreated}
      />
    </div>
  );
}

